/**
 * Group C — members (`docs/api-v1/04-v1-surface.md` §3.1 C).
 *
 *     GET   /v1/communities/{skool_id}/members              the explorer
 *     GET   /v1/communities/{skool_id}/members/{member_id}   one member, any depth
 *     GET   /v1/communities/{skool_id}/members/{member_id}/dms  the mirrored DM thread
 *     GET   /v1/communities/{skool_id}/roles                 the {member_id → quadrant} map
 *
 * WHY THIS FILE IS THE POINT OF WAVE 6. `/admin/members` is the biggest over-fetch
 * in the app (doc 02 §4.5, doc 04 §5.1): every one of 50 rows carries a DENSE
 * 371-day heatmap — 20,423 bytes per member, ≈1,021 KB per page — rendered into a
 * table cell whose `<Heatmap>` caps its cells at 18 px, and 11 of the row's 29
 * fields are never rendered (550 dead fields per page).
 *
 * v1 fixes it with selection, not with a new route:
 *
 *     default field set                    ≈28 KB / 50 rows
 *     ?include=activity&activity=weekly    ≈93 KB / 50 rows   ← what this file asks for
 *     ?include=activity&activity=dense   ≈1,021 KB / 50 rows   (what ships today)
 *
 * `weekly` is 53 × `{week, total}`. The members table never needed a day grid: it
 * renders one narrow cell per row, and 53 weekly buckets say the same thing at 1/16
 * of the size. `dense` exists on the wire only because the FROZEN member-card's
 * `heatmap.days` must stay contiguous (`Heatmap.svelte:45-52` shifts every cell into
 * the wrong weekday otherwise) — no admin surface should ask for it.
 *
 * SHAPES ARE NESTED HERE, FLAT ON THE LEGACY ROUTE. `/admin/members` returns 29 flat
 * columns; v1 returns §2.1's blocks — `membership`, `stats`, `journey`, `activity` —
 * and an unselected block is OMITTED, not null (`response_model_exclude_unset`), which
 * is why every block below is optional. `membership` satisfies `MemberPlan` and
 * `MemberRenewal` structurally, so `<PlanCell>` and `<RenewalCell>` take it unchanged.
 */

import { apiGet } from './http';
import { community } from './envelope';
import type { CommunityRef, ListEnvelope, QueryParams } from './envelope';

import type { MemberRef } from './shapes';
export type { MemberRef };
import type {
  MemberAnalytics,
  MemberStage,
  MemberStatus,
  PlanInterval,
  RoleJourneySegment,
} from './shapes';


// `<RoleJourney>` and the member hovercard live in `@ui`, so these came with them.
// Re-exported from here because a caller reading members wants the member's journey
// in the same import.
export { journeySegments, type JourneyStep, type MemberCard, type StatsBlock } from '@ui/member';
export type { DmAttachment, DmVideo } from '@ui';
import type { DmAttachment, DmVideo } from '@ui';
import { fetchMemberCard as fetchCard } from '@ui/member';

/** The hovercard route, typed as the fuller member this app reads off it. Same
 *  request either way — `@ui/member` owns the path and the params. */
export function fetchMemberCard(
  skoolId: number,
  memberId: number,
): Promise<Member<DenseActivity>> {
  return fetchCard<Member<DenseActivity>>(skoolId, memberId);
}
import type { JourneyStep, StatsBlock } from '@ui/member';

// ---------------------------------------------------------------------------
// §2.1 — the one member representation
// ---------------------------------------------------------------------------

/** The author card — five fields, one definition, inside every list row. `handle`
 *  is genuinely nullable on the wire (`identity_of` can hold none); the legacy
 *  `PostAuthor` types it as a bare string, which is what `toAuthor` reconciles. */

/** The billing/lifecycle block — default-on for `…/members`. Every field is null in
 *  a community we hold no staff role in, which is how the client tells "free" from
 *  "we cannot see". Structurally a `MemberPlan` AND a `MemberRenewal`. */
export interface MembershipBlock {
  status: MemberStatus | null;
  mrr_status: string | null;
  plan_currency: string | null;
  plan_amount_cents: number | null;
  plan_interval: PlanInterval | null;
  plan_tier: string | null;
  ends_at: string | null;
  cancelled_at: string | null;
  departed_at: string | null;
  /** The four the members TABLE has no column for, so they were left undeclared when
   *  this interface was written for the list. The route emits them on every member
   *  (verified against `api/v1/members.py`'s `MembershipOut`), and the profile page
   *  renders all four — `attribution` beside the join date, `roster_synced_at` as the
   *  "last confirmed" note, `billing_email` in the billing strip. Declaring them here
   *  is what lets `fetchMemberAnalytics` hand the block over unchanged. */
  billing_email: string | null;
  invited_by: string | null;
  attribution: string | null;
  roster_synced_at: string | null;
  /** OWNER-ENTERED, and served only by `GET …/members/{member_id}` — the `detail`
   *  branch. Absent from the members LIST and from every pipeline card, deliberately:
   *  the base branch ships on 50 rows at a time and there is no reader that wants a
   *  phone number in bulk. Scoped to ONE community, so what another owner typed about
   *  the same Skool user is not this value and is not reachable from here. */
  contact_email: string | null;
  contact_phone: string | null;
  contact_updated_at: string | null;
  contact_updated_by: string | null;
}


/** `?include=activity&activity=weekly` — 53 buckets, ≈1,300 B/row. `week` is the
 *  bucket's FIRST DAY (`YYYY-MM-DD`), Sunday-aligned like the heatmap it replaces.
 *  `max` is the peak WEEK's total, not the peak day's. */
export interface WeeklyActivity {
  weeks: { week: string; total: number }[];
  max: number;
}

/** `activity=dense` — the 371-day contiguous series, ≈20 KB. Exactly `HeatmapDay[]`,
 *  which is what `<Heatmap>` requires: it derives the weekday column from the FIRST
 *  day's index and pads from there, so a gapped series shifts every cell into the
 *  wrong column for the rest of the year (`Heatmap.svelte:45-52`). Ask for it only
 *  where a day grid is actually rendered — the hovercard — never in a list. */
export interface DenseActivity {
  days: { date: string; posts: number; comments: number; total: number }[];
  max: number;
}

/** `activity=sparse` — non-zero days only, and NO `total` per day (it is
 *  `posts + comments`). Not a heatmap input; it is the compact form for a caller
 *  that wants the dates something happened. */
export interface SparseActivity {
  days: { date: string; posts: number; comments: number }[];
  max: number;
}

/** The activity block, in whichever of §5.1's three shapes was asked for. Narrow it
 *  by the shape you requested — the wire carries no discriminator, because the caller
 *  chose the shape. */
export type ActivitySeries = WeeklyActivity | DenseActivity | SparseActivity;

/** One member, at whatever depth was asked for. `A` is the shape of the activity
 *  block the CALLER requested — the wire carries no discriminator because the caller
 *  chose it, so the choice is expressed in the type instead of re-derived at every
 *  render site. It defaults to `WeeklyActivity`, which is what every list asks for. */
export interface Member<A extends ActivitySeries = WeeklyActivity> extends MemberRef {
  location?: string | null;
  role?: string | null;
  /** Emitted on every member; declared here rather than on `MemberRef` because the
   *  ref is the compact shape reused inside other resources, and only the member
   *  itself carries a profile. The profile page renders both. */
  links?: Record<string, string> | null;
  joined_at?: string | null;
  membership?: MembershipBlock;
  stats?: StatsBlock | null;
  journey?: JourneyStep[];
  activity?: A;
  depth?: DepthBlock;
  analysis?: AnalysisBlock;
}

/** `?include=depth` — the two figures that only mean anything relative to the rest of
 *  the community, plus the bio. `rank` is 1-based within `contributor_pool`. */
export interface DepthBlock {
  rank: number | null;
  contributor_pool: number | null;
  bio: string | null;
}

/** `?include=analysis` — the member profile's four charts.
 *
 *  `window` is the ONLY windowed thing here and it is why this block exists separately
 *  from `stats`: `stats` is all-time (a stage and a momentum computed over a fixed
 *  contributor window), whereas the profile's headline counters move with the page's
 *  day selector. `trend` and `clock` are all-time whatever `?last=` says — a weekly
 *  chart cropped to 30 days is one bar — and only `reply_network` is scoped by it. */
export interface AnalysisBlock {
  trend: { week: string; posts: number; comments: number }[];
  clock: { weekday: number; hour: number; count: number }[];
  window: {
    posts: number;
    comments: number;
    activity: number;
    upvotes_received: number;
    first_active: string | null;
    last_active: string | null;
  };
  /** Nested `member` rather than flattened ids — §2.1's member shape, one definition
   *  reused. The legacy `ReplyPartner` flattened it; `fetchMemberAnalytics` below
   *  puts it back for the existing render sites. */
  reply_network: { member: MemberRef; sent: number; got: number }[];
  reply_totals: { given: number; received: number; partners: number; mutual: number };
}

/** `total` is how many rows match the filters; `total_members` is the community's
 *  headcount from the roster sweep. Conflating the two is where "99 of 86 members"
 *  came from, which is why they sit side by side rather than one being derived. */
export interface MembersPage extends ListEnvelope<Member> {
  community: CommunityRef;
  total_members: number | null;
}

export interface MemberResource<A extends ActivitySeries = WeeklyActivity> {
  community: CommunityRef;
  member: Member<A>;
}

// ---------------------------------------------------------------------------
// The explorer
// ---------------------------------------------------------------------------

/** Every filter the members table exposes. All of them are server-side, all of the
 *  multi-selects are REPEATED parameters (`?stage=rising&stage=steady`), and an
 *  unrecognised value is a 400 rather than a silent drop (§1.6). */
export interface MemberQuery {
  q?: string;
  sort?: MemberSort;
  order?: 'asc' | 'desc';
  /** `exclude` (default) is today's `/admin/members`; `only` is `/admin/moderators`;
   *  `all` is the members table's "include moderators" toggle. */
  staff?: 'exclude' | 'only' | 'all';
  quadrant?: string[];
  had_role?: string[];
  stage?: string[];
  style?: string[];
  status?: string[];
  tier?: string;
  /** `YYYY-MM` first-contribution cohort, from the retention heatmap's deep link. */
  cohort?: string;
  retained?: number;
  limit?: number;
  offset?: number;
  /** Ask for the weekly series. Omitted, no activity block is returned at all. */
  withActivity?: boolean;
}

/** The sortable columns, as the route declares them. Same nine names the legacy
 *  `MemberSort` uses — the table's header buttons need no translation. */
export type MemberSort =
  | 'activity'
  | 'posts'
  | 'comments'
  | 'upvotes_received'
  | 'replies_earned'
  | 'momentum'
  | 'last_active'
  | 'first_active'
  | 'handle'
  | 'ends_at';

function listParams(opts: MemberQuery): QueryParams {
  // `journey` is a DEFAULT include, and sending any `?include=` at all replaces the
  // defaults — so asking for `activity` means naming `journey` again or losing the
  // role-journey pipe every row renders.
  const include = opts.withActivity ? ['journey', 'activity'] : undefined;
  return {
    q: opts.q?.trim() || undefined,
    sort: opts.sort,
    order: opts.order,
    staff: opts.staff,
    quadrant: opts.quadrant?.length ? opts.quadrant : undefined,
    had_role: opts.had_role?.length ? opts.had_role : undefined,
    stage: opts.stage?.length ? opts.stage : undefined,
    style: opts.style?.length ? opts.style : undefined,
    status: opts.status?.length ? opts.status : undefined,
    tier: opts.tier || undefined,
    cohort: opts.cohort || undefined,
    retained: opts.retained ? opts.retained : undefined,
    limit: opts.limit,
    offset: opts.offset,
    include,
    ...(opts.withActivity ? { activity: 'weekly' } : {}),
  };
}

/** One page of the members explorer. Offset-paginated — one of §1.3's three declared
 *  offset routes, because the table sorts by an arbitrary column and pages with
 *  prev/next and a page count. */
export function fetchMembers(skoolId: number, opts: MemberQuery = {}): Promise<MembersPage> {
  return apiGet<MembersPage>(community(skoolId, '/members'), listParams(opts));
}

/**
 * One member, with the weekly activity series when asked for.
 *
 * The route collapses `/admin/analytics/member` and `/admin/analytics/member-card`
 * into one resource — but only for the blocks it can serve: `?include=analysis`
 * (trend / clock / reply-network) and `?include=depth` (rank / contributor_pool /
 * bio) answer 400 `unknown_include` today, because neither aggregation exists in
 * `domain/` yet. The member PROFILE page therefore still reads its analytics from
 * `GET /admin/analytics/member`; the hovercard does not need any of it and uses
 * `fetchMemberCard` below.
 */
export function fetchMember(
  skoolId: number,
  memberId: number,
  opts: { withActivity?: boolean } = {},
): Promise<MemberResource<WeeklyActivity>> {
  return apiGet<MemberResource<WeeklyActivity>>(community(skoolId, `/members/${memberId}`), {
    ...(opts.withActivity ? { include: ['journey', 'activity'], activity: 'weekly' } : {}),
  });
}


/**
 * The member PROFILE page, in one request — the last legacy analytics read to move.
 *
 * WHY THIS RETURNS THE LEGACY SHAPE. `AdminMember.svelte` reads eighteen paths off
 * `MemberAnalytics` across four charts, a stat strip, a membership panel and a role
 * journey. Re-pointing all of them at the v1 nesting would be a large diff through
 * markup that no type error can check and no test covers, for no user-visible gain.
 * So the mapping lives here, once, and the page is a one-line change. This is the same
 * `…Wire` → domain-shape pattern `@api/v1/moderation` and `@api/v1/pipelines`
 * already use; it is a translation layer, not a compatibility shim to keep forever.
 *
 * WHY IT COULD NOT HAPPEN UNTIL NOW. `?include=analysis` and `?include=depth` answered
 * 400 `unknown_include` until `domain.activity.member_analysis` and
 * `domain.contributors.member_rank` were written — the gap this file used to document
 * a few lines up. `/admin/analytics/member` is gone now that they exist.
 *
 * THE ONE REAL DIFFERENCE, and it is a fix rather than a regression: the legacy route
 * emitted `last_active` as a bare `YYYY-MM-DD` here and as an ISO instant on
 * `member-card`, and the profile page fetched both (doc 04 §1.9). v1 emits instants
 * everywhere, so anything rendering these must go through `parseTs`, which
 * `AdminMember.svelte` already does.
 */
export async function fetchMemberAnalytics(
  skoolId: number,
  memberId: number,
  days = 30,
): Promise<MemberAnalytics> {
  const { member } = await apiGet<MemberResource<DenseActivity>>(
    community(skoolId, `/members/${memberId}`),
    {
      // `dense` is required, not a preference: <Heatmap> needs a contiguous ascending
      // series or every cell lands in the wrong weekday column (doc 03 §3.4).
      include: ['journey', 'activity', 'analysis', 'depth'],
      activity: 'dense',
      // `days === 0` is the PERIOD SELECTOR'S "ALL TIME", not zero days — and it is
      // the member page's default (`AdminMember.svelte`). Interpolating it blindly
      // produced `last=0d`, which `_parse_last` rejects (it requires N > 0), so every
      // load of the profile page answered 400 `invalid_time_window`. v1 spells
      // all-time as the literal `all`; `@api/v1/insights` already translates it that
      // way in two places and this call site was the one that did not.
      last: days > 0 ? `${days}d` : 'all',
    },
  );
  const a = member.analysis;
  const stats = member.stats;
  return {
    member: {
      id: member.member_id,
      // Same nullable-handle reconciliation `toAuthor` does in `@api/v1/posts`.
      handle: member.handle ?? '',
      name: member.name,
      picture_profile: member.picture_profile ?? null,
      bio: member.depth?.bio ?? null,
      location: member.location ?? null,
      mrr_status: member.membership?.mrr_status ?? null,
      role: member.role ?? null,
      links: member.links ?? null,
      joined_at: member.joined_at ?? null,
      last_seen_at: stats?.last_active ?? null,
      membership: member.membership ?? null,
    },
    window_days: days,
    // The windowed half comes from `analysis.window`, the classification half from
    // `stats`. Reading counters off `stats` instead would silently make the page's day
    // selector do nothing, since `stats` is all-time.
    summary: {
      posts: a?.window.posts ?? 0,
      comments: a?.window.comments ?? 0,
      activity: a?.window.activity ?? 0,
      upvotes_received: a?.window.upvotes_received ?? 0,
      replies_given: a?.reply_totals.given ?? 0,
      replies_received: a?.reply_totals.received ?? 0,
      first_active: a?.window.first_active ?? null,
      last_active: a?.window.last_active ?? null,
      style: stats?.style ?? null,
      quadrant: stats?.quadrant ?? null,
      stage: (stats?.stage ?? 'newcomer') as MemberStage,
      momentum: stats?.momentum ?? 0,
    },
    role_journey: (member.journey ?? []) as RoleJourneySegment[],
    heatmap: { days: member.activity?.days ?? [], max: member.activity?.max ?? 0 },
    trend: a?.trend ?? [],
    clock: a?.clock ?? [],
    // Flatten §2.1's nested member back to the legacy partner row the table renders.
    reply_network: (a?.reply_network ?? []).map((p) => ({
      member_id: p.member.member_id,
      // §2.1's handle is nullable (a Skool account can have none); the legacy partner
      // row was typed non-null and the table renders it raw, so the empty string is the
      // rendered value rather than the string "null".
      handle: p.member.handle ?? '',
      name: p.member.name,
      picture_profile: p.member.picture_profile ?? null,
      sent: p.sent,
      got: p.got,
    })),
    reply_totals: a?.reply_totals ?? { given: 0, received: 0, partners: 0, mutual: 0 },
  };
}

// ---------------------------------------------------------------------------
// The role map
// ---------------------------------------------------------------------------

/**
 * §1.2's ONE map exception: `roles` is `member_id → quadrant` for the whole
 * community — a lookup table, not a list of entities — so it is not paginated and it
 * does not use the list envelope.
 *
 * The keys are DECIMAL MEMBER IDS AS STRINGS, because JSON object keys are strings by
 * specification. That is not a convention violation waiting to be tidied: the reader
 * is `roles.map[String(memberId)]` (`lib/roles.svelte.ts`), and "cleaning up" to
 * integers would make every avatar ring disappear silently.
 *
 * Departed members carry the terminal `departed` value; staff and members with no
 * current role are ABSENT, and the client already renders a missing id as no ring.
 */
export interface RoleMap {
  /** New in v1 — the legacy route answered `{roles}` alone. Nothing has to read it;
   *  it is there so a cached map can be checked against the community it came from. */
  community: CommunityRef;
  roles: Record<string, string>;
}

/**
 * `GET /v1/communities/{skool_id}/roles` — every current contributor's quadrant,
 * loaded once per community so any `<Avatar>` can wear a role-coloured ring without
 * the quadrant being threaded through every payload.
 *
 * Shadows the FROZEN `GET /admin/analytics/member-roles`, which the Helper keeps
 * calling (3,462 requests from confirmed installs in 14 days — doc 07 §2.3); the two
 * are the same map from the same `windowed_role_map_cached` call, so a community's
 * rings and badges agree across both surfaces.
 *
 * The one difference a caller sees: `roles` is now nested beside a `community` block
 * instead of being the whole body, so `fetchMemberRoles(id)` → `fetchRoles(id)` and
 * `r.roles` is unchanged. Key order is now deterministic (member-id ascending); it
 * varied between identical calls before, which nothing depended on but which made
 * two responses diff.
 */
export function fetchRoles(skoolId: number): Promise<RoleMap> {
  return apiGet<RoleMap>(community(skoolId, '/roles'));
}

// ---------------------------------------------------------------------------
// Adapters to the shared components
// ---------------------------------------------------------------------------


/** A member with no `membership` block — a row from a community we hold no staff role
 *  in, or a route that did not select the block. `status: null` is what makes
 *  `<PlanCell>` render `—` ("we do not know") rather than `Free` ("they pay nothing"),
 *  so this is the honest empty, not a placeholder. */
export const NO_MEMBERSHIP: MembershipBlock = {
  status: null,
  mrr_status: null,
  plan_currency: null,
  plan_amount_cents: null,
  plan_interval: null,
  plan_tier: null,
  ends_at: null,
  cancelled_at: null,
  departed_at: null,
  billing_email: null,
  invited_by: null,
  attribution: null,
  roster_synced_at: null,
  contact_email: null,
  contact_phone: null,
  contact_updated_at: null,
  contact_updated_by: null,
};

// ---------------------------------------------------------------------------
// DM history (`GET …/members/{member_id}/dms`)
// ---------------------------------------------------------------------------

/**
 * One mirrored message. `outbound` is resolved server-side (the sender is not the
 * member) so no caller has to know which id belongs to whom to pick a bubble side.
 */
export interface DmThreadMessage {
  external_id: string;
  content: string;
  created_at: string | null;
  outbound: boolean;
  /** The two media columns, straight through. Typed by `@ui` and not here: this
   *  route, `/v1/me/dms/*` and the superadmin explorer all project the same stored
   *  JSON, and one client component draws all three — a copy per route is how the
   *  three drift, which is what #161 was cleaning up. */
  videos: DmVideo[] | null;
  attachments: DmAttachment[] | null;
}

/**
 * One conversation with this member, from one owner's inbox.
 *
 * `owner_*` is attribution for the superadmin read, where threads from several
 * inboxes arrive in one response. It is present for a creator too — their own id —
 * so a component renders one shape instead of branching on who is looking.
 *
 * `origin_skool_id` is the community the conversation came THROUGH, which is
 * usually not the community being viewed and is null for the majority: a Skool
 * account has one inbox spanning every community it touches.
 */
export interface DmThread {
  channel_external_id: string;
  /**
   * WHO this conversation is unread for, per Skool (#180) — the read receipt for what
   * the owner sent.
   *
   * Three states and they are not interchangeable: a list containing the counterpart
   * is unread BY THEM, an empty list is read, and `null` is never recorded (channels
   * that predate the column, which cannot be backfilled). Null must render as unknown
   * and never as read — it is a claim about somebody else's behaviour.
   *
   * Not a timestamp and not per message: Skool reports it per conversation, so it says
   * "they are up to date", not "they opened this one".
   */
  unread_user_external_ids?: string[] | null;
  owner_user_id: number;
  owner_name: string | null;
  owner_email: string | null;
  origin_skool_id: number | null;
  counterpart_handle: string | null;
  counterpart_name: string | null;
  counterpart_picture: string | null;
  /** The owner's Skool avatar — the other half of `counterpart_picture`. */
  owner_picture: string | null;
  last_message_at: string | null;
  message_count: number;
  has_more: boolean;
  messages: DmThreadMessage[];
}

export interface MemberDmThreads extends ListEnvelope<DmThread> {
  community: CommunityRef;
  member: MemberRef | null;
}

/**
 * The DM history with one member.
 *
 * Keyed on the MEMBER, not on a pipeline card: a card is a CRM artifact that comes
 * and goes with a pipeline definition, while the conversation belongs to the person.
 * The card drawer is the first caller; the member page is expected to call this same
 * function with the same arguments.
 *
 * WHOSE MESSAGES COME BACK IS NOT THIS CALLER'S DECISION. The server reads the
 * caller's role — a creator gets their own inbox, a superadmin gets the inbox of the
 * owner of the community in the path. There is deliberately no parameter for it: a
 * client-supplied "show me everyone's" would be an authorization bypass.
 *
 * A superadmin used to receive EVERY owner's inbox, flagged by an `all_owners` field
 * that is gone with it. It put a row of inbox tabs on the card drawer carrying threads
 * from communities the reader was not looking at.
 *
 * `limit` caps messages PER THREAD and keeps the most recent; each thread still
 * reports its true `message_count`, so `has_more` drives a "load earlier" affordance
 * rather than silently truncating.
 */
export function fetchMemberDms(
  skoolId: number,
  memberId: number,
  opts: { limit?: number } = {},
): Promise<MemberDmThreads> {
  return apiGet<MemberDmThreads>(community(skoolId, `/members/${memberId}/dms`), {
    ...(opts.limit ? { limit: opts.limit } : {}),
  });
}
